import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, IconButton, Typography, Box,
  Table, TableHead, TableRow, TableCell, TableBody,
  Switch, Chip, Tooltip, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import LayersIcon from '@mui/icons-material/Layers';
import { fetchData, postData, updateData, deleteData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';

const emptyRow = () => ({
  _key: Date.now() + Math.random(),
  Id: null,
  MasterId: '',
  ProfitType: true,   // true = Percentage, false = Fixed
  MinProfitRate: '',
  MaxProfitRate: '',
  FreightAmount: '',
  OtherAmount: '',
  Remarks: '',
  IsActive: true,
  isDefault: false,
});

// Inline toggle button group for Profit Type
const ProfitTypeToggle = ({ value, onChange }) => (
  <ToggleButtonGroup
    exclusive
    size="small"
    value={value ? 'pct' : 'fixed'}
    onChange={(_, v) => { if (v !== null) onChange(v === 'pct'); }}
    sx={{
      height: '24px',
      '& .MuiToggleButton-root': {
        fontSize: '11px', fontWeight: '700', textTransform: 'none',
        padding: '0 10px', border: '1px solid #d0d0d0', lineHeight: 1,
        color: '#666', whiteSpace: 'nowrap',
        '&.Mui-selected': {
          color: 'white',
          backgroundColor: '#2C3E6E',
          borderColor: '#2C3E6E',
          '&:hover': { backgroundColor: '#1a2a4a' },
        },
      },
    }}
  >
    <ToggleButton value="pct">%</ToggleButton>
    <ToggleButton value="fixed">₹</ToggleButton>
  </ToggleButtonGroup>
);

const CustomerRateSlabModal = ({ open, onClose, onSave, item, masters = [] }) => {
  const { companyId, userId, token } = useAuth();
  const { logAudit } = useAuditLog();
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadRows = () => {
    if (!item) return;
    setLoading(true);
    setRows([]);
    fetchData('CustomerRateSlabApi', `/${companyId}/${item.Id}`, token)
      .then(res => {
        const existing = res?.responseCode === '1111' ? res.response_data : [];
        const hasDefault = existing.some(r => r.MasterId == null);
        const mapped = existing.map(r => ({
          _key: r.Id,
          Id: r.Id,
          MasterId: r.MasterId ?? '',
          ProfitType: r.ProfitType ?? true,
          MinProfitRate: r.MinProfitRate ?? '',
          MaxProfitRate: r.MaxProfitRate ?? '',
          FreightAmount: r.FreightAmount ?? '',
          OtherAmount: r.OtherAmount ?? '',
          Remarks: r.Remarks ?? '',
          IsActive: r.IsActive ?? true,
          isDefault: r.MasterId == null,
        }));
        if (!hasDefault) {
          mapped.unshift({ ...emptyRow(), isDefault: true, MasterId: '', _key: 'default' });
        }
        setRows(mapped);
      })
      .catch(() => {
        setRows([{ ...emptyRow(), isDefault: true, MasterId: '', _key: 'default' }]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open || !item) return;
    loadRows();
  }, [open, item]);

  const updateRow = (key, field, value) =>
    setRows(prev => prev.map(r => r._key === key ? { ...r, [field]: value } : r));

  const addRow = () => setRows(prev => [...prev, emptyRow()]);

  const handleDeleteRow = async (row) => {
    if (row.Id) {
      try { await deleteData('CustomerRateSlabApi', row.Id, token); } catch (e) { console.error(e); }
    }
    setRows(prev => prev.filter(r => r._key !== row._key));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payloads = rows.map(row => {
        const payload = {
          ItemId: item.Id,
          MasterId: row.isDefault || row.MasterId === '' ? null : parseInt(row.MasterId),
          ProfitType: row.ProfitType,
          MinProfitRate: parseFloat(row.MinProfitRate) || 0,
          MaxProfitRate: parseFloat(row.MaxProfitRate) || 0,
          FreightAmount: parseInt(row.FreightAmount) || 0,
          OtherAmount: parseInt(row.OtherAmount) || 0,
          Remarks: row.Remarks || null,
          IsActive: row.IsActive,
          CompanyId: companyId,
        };
        if (row.Id) {
          payload.Id = row.Id;
          payload.ModifiedBy = userId;
          payload.ModifiedDate = new Date();
        } else {
          payload.CreatedBy = userId;
          payload.CreatedDate = new Date();
        }
        return payload;
      });
      await postData('CustomerRateSlabApi', '', payloads, token);
      logAudit(AUDIT_ACTION.CREATE, 'CustomerRateSlabApi', 0, null, { Count: payloads.length });
      onSave();
      loadRows();
    } catch (err) {
      console.error('Error saving slabs:', err);
    } finally {
      setSaving(false);
    }
  };

  const hCell = {
    color: 'white', fontSize: '11px', fontWeight: '600',
    padding: '6px 8px', whiteSpace: 'nowrap', letterSpacing: '0.3px'
  };
  const cell = { padding: '5px 6px', verticalAlign: 'middle' };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
      PaperProps={{
        style: {
          borderRadius: '12px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '2px',
        }
      }}>
      <Box style={{ background: 'white', borderRadius: '10px', overflow: 'hidden' }}>

        {/* Title */}
        <DialogTitle style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white', padding: '10px 14px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <LayersIcon style={{ fontSize: '18px' }} />
            <Box>
              <Typography style={{ fontWeight: '700', fontSize: '14px', lineHeight: 1.2 }}>
                Customer Rate Slabs
              </Typography>
              <Typography style={{ fontSize: '11px', opacity: 0.85, lineHeight: 1.2 }}>
                {item?.Name} {item?.Code ? `(${item.Code})` : ''}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} style={{ color: 'white', padding: '4px' }} size="small">
            <CloseIcon style={{ fontSize: '18px' }} />
          </IconButton>
        </DialogTitle>

        <DialogContent style={{ padding: '14px 14px 8px' }}>
          {loading ? (
            <Typography style={{ textAlign: 'center', padding: '30px', color: '#999', fontSize: '13px' }}>
              Loading slabs...
            </Typography>
          ) : (
            <>
              {/* Legend */}
              <Box style={{
                display: 'flex', gap: '16px', alignItems: 'center',
                marginBottom: '10px', padding: '6px 10px',
                background: '#f8f9ff', borderRadius: '6px', border: '1px solid #e8eaf6'
              }}>
                <Typography style={{ fontSize: '11px', color: '#555' }}>
                  <strong style={{ color: '#1976d2' }}>Default</strong> row applies to all customers without a specific slab.
                </Typography>
                <Box style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
                  <Chip size="small"
                    label="% Percentage" style={{ fontSize: '10px', height: '20px', background: '#e3f2fd', color: '#1565c0' }} />
                  <Chip size="small"
                    label="₹ Fixed Amt" style={{ fontSize: '10px', height: '20px', background: '#fff3e0', color: '#e65100' }} />
                </Box>
              </Box>

              <Box style={{ overflowX: 'auto' }}>
                <Table size="small" style={{ minWidth: 860 }}>
                  <TableHead>
                    <TableRow style={{ background: '#2C3E6E' }}>
                      <TableCell style={hCell}>Customer</TableCell>
                      <TableCell style={{ ...hCell, textAlign: 'center', width: '80px' }}>Profit Type</TableCell>
                      <TableCell style={{ ...hCell, width: '80px' }}>Min Rate</TableCell>
                      <TableCell style={{ ...hCell, width: '80px' }}>Max Rate</TableCell>
                      <TableCell style={{ ...hCell, width: '75px' }}>Freight</TableCell>
                      <TableCell style={{ ...hCell, width: '75px' }}>Other Amt</TableCell>
                      <TableCell style={hCell}>Remarks</TableCell>
                      <TableCell style={{ ...hCell, textAlign: 'center', width: '55px' }}>Active</TableCell>
                      <TableCell style={{ ...hCell, width: '32px' }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, idx) => (
                      <TableRow key={row._key} style={{
                        background: row.isDefault
                          ? '#f0f4ff'
                          : idx % 2 === 0 ? '#fff' : '#fafafa',
                        borderLeft: row.isDefault ? '3px solid #1976d2' : '3px solid transparent',
                      }}>

                        {/* Customer */}
                        <TableCell style={cell}>
                          {row.isDefault ? (
                            <Chip label="Default" size="small"
                              style={{
                                fontSize: '10px', height: '22px', fontWeight: '700',
                                background: '#1976d2', color: 'white', minWidth: '70px'
                              }} />
                          ) : (
                            <TextField select size="small" value={row.MasterId}
                              onChange={e => updateRow(row._key, 'MasterId', e.target.value)}
                              SelectProps={{ native: true }} fullWidth
                              sx={{
                                minWidth: '150px',
                                '& .MuiOutlinedInput-root': { fontSize: '12px', height: '28px' }
                              }}
                            >
                              <option value="">— Select Customer —</option>
                              {masters.map(m => <option key={m.MasterId} value={m.MasterId}>{m.Name}</option>)}
                            </TextField>
                          )}
                        </TableCell>

                        {/* Profit Type — toggle button group */}
                        <TableCell style={{ ...cell, textAlign: 'center' }}>
                          <ProfitTypeToggle
                            value={row.ProfitType}
                            onChange={v => updateRow(row._key, 'ProfitType', v)}
                          />
                        </TableCell>

                        {/* Min Rate */}
                        <TableCell style={cell}>
                          <TextField size="small" value={row.MinProfitRate}
                            onChange={e => updateRow(row._key, 'MinProfitRate', e.target.value)}
                            placeholder="0.00" fullWidth
                            inputProps={{ style: { textAlign: 'right' } }}
                            sx={{ '& .MuiOutlinedInput-root': { fontSize: '12px', height: '28px' } }}
                          />
                        </TableCell>

                        {/* Max Rate */}
                        <TableCell style={cell}>
                          <TextField size="small" value={row.MaxProfitRate}
                            onChange={e => updateRow(row._key, 'MaxProfitRate', e.target.value)}
                            placeholder="0.00" fullWidth
                            inputProps={{ style: { textAlign: 'right' } }}
                            sx={{ '& .MuiOutlinedInput-root': { fontSize: '12px', height: '28px' } }}
                          />
                        </TableCell>

                        {/* Freight */}
                        <TableCell style={cell}>
                          <TextField size="small" value={row.FreightAmount}
                            onChange={e => updateRow(row._key, 'FreightAmount', e.target.value)}
                            placeholder="0" fullWidth
                            inputProps={{ style: { textAlign: 'right' } }}
                            sx={{ '& .MuiOutlinedInput-root': { fontSize: '12px', height: '28px' } }}
                          />
                        </TableCell>

                        {/* Other */}
                        <TableCell style={cell}>
                          <TextField size="small" value={row.OtherAmount}
                            onChange={e => updateRow(row._key, 'OtherAmount', e.target.value)}
                            placeholder="0" fullWidth
                            inputProps={{ style: { textAlign: 'right' } }}
                            sx={{ '& .MuiOutlinedInput-root': { fontSize: '12px', height: '28px' } }}
                          />
                        </TableCell>

                        {/* Remarks */}
                        <TableCell style={cell}>
                          <TextField size="small" value={row.Remarks}
                            onChange={e => updateRow(row._key, 'Remarks', e.target.value)}
                            placeholder="Optional" fullWidth
                            inputProps={{ maxLength: 150 }}
                            sx={{ '& .MuiOutlinedInput-root': { fontSize: '12px', height: '28px' } }}
                          />
                        </TableCell>

                        {/* Active */}
                        <TableCell style={{ ...cell, textAlign: 'center' }}>
                          <Switch size="small" checked={row.IsActive}
                            onChange={e => updateRow(row._key, 'IsActive', e.target.checked)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': { color: '#2C3E6E' },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#2C3E6E' },
                            }}
                          />
                        </TableCell>

                        {/* Delete */}
                        <TableCell style={cell}>
                          {!row.isDefault && (
                            <Tooltip title="Remove row" arrow>
                              <IconButton size="small" onClick={() => handleDeleteRow(row)}
                                style={{ color: '#e53e3e', padding: '3px' }}>
                                <DeleteIcon style={{ fontSize: '15px' }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>

              <Button
                startIcon={<AddIcon style={{ fontSize: '14px' }} />}
                onClick={addRow}
                size="small"
                style={{
                  marginTop: '10px', textTransform: 'none', fontSize: '12px',
                  color: '#667eea', fontWeight: '600', padding: '4px 10px',
                  border: '1px dashed #667eea', borderRadius: '6px',
                }}
              >
                Add Customer Row
              </Button>
            </>
          )}
        </DialogContent>

        <DialogActions style={{
          padding: '8px 14px', background: '#f8f9fa',
          borderTop: '1px solid #e9ecef', gap: '8px'
        }}>
          <Typography style={{ fontSize: '11px', color: '#999', flex: 1 }}>
            {rows.length} slab{rows.length !== 1 ? 's' : ''} configured
          </Typography>
          <Button onClick={onClose}
            style={{
              color: '#6c757d', textTransform: 'none', fontWeight: '500',
              fontSize: '12px', padding: '4px 12px'
            }}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} variant="contained"
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white', textTransform: 'none', fontWeight: '600',
              borderRadius: '6px', padding: '5px 16px', fontSize: '12px'
            }}>
            {saving ? 'Saving...' : 'Save All'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default CustomerRateSlabModal;

